import { useCallback, useMemo } from "react";
import { useUIDSeed } from "react-uid";
import { useRouter } from "next/router";

import { initializeGlobalProps } from "@hmn/coolinarika-web-core/helpers";
import {
    getListElastic,
    InfiniteLoadTrigger,
    lg,
    useBreakpoints,
    useImageUrl,
    useListElastic,
    usePageUrl,
    useQueryState,
    useSearchAnalytics,
} from "@hmn/coolinarika-web-core/hooks";
import { storeWrapper } from "@hmn/coolinarika-web-core/store";

import {
    HeadingSectionHero,
    headingSectionHeroSizes,
} from "@hmn/coolinarika-web-ui/components/HeadingSection";
import { ListingSeo } from "@hmn/coolinarika-web-ui/components/Listing/components";
import { FooterSeparator } from "@hmn/coolinarika-web-ui/components/Misc/components";
import { NavigationTopBar } from "@hmn/coolinarika-web-ui/components/Navigation";
import {
    HeadingResultsSimple,
    SearchNoResults,
    SingleSearchSimple,
} from "@hmn/coolinarika-web-ui/components/Search/components";
import { GridLayoutController } from "@hmn/coolinarika-web-ui/controllers";

import { Motion } from "@web/components";
import { Column, Row } from "@web/layouts";

const title = "Blog";
const description =
    // eslint-disable-next-line max-len
    "Članci, blogovi, savjeti, zanimljive teme. Naći ćeš ovdje korisnih stvari: od kulinarskih tema preko zdravstvenih članaka pa sve do putopisa i kuharskih dnevnika. Vrijedi ti baciti pogled.";
const desktopImageId = "215893be-7063-11eb-b1aa-269cf4c691c9";
const otherDevicesImageId = `758fa88e-7066-11eb-867f-269cf4c691c9`;

const Articles = ({ ...rest }) => {
    const uid = useUIDSeed();
    const [isDesktop] = useBreakpoints(lg);
    const { replace } = useRouter();

    const [{ upit: searchTerm }, setSearchTermQuery] = useQueryState({ upit: "" });

    const url = usePageUrl();

    const gtmData = { url, title };

    const { pages, ref } = useListElastic({
        resource: "blog",
        endpoint: "novo",
        gtmData,
    });

    const {
        pages: searchPages,
        ref: searchRef,
        isFetching: isSearching,
    } = useListElastic({
        resource: "blog",
        endpoint: "pretrazivanje",
        params: {
            query: {
                search: searchTerm,
            },
        },
        enabled: !!searchTerm,
        gtmData,
    });

    useSearchAnalytics("Blogs search", isSearching, searchTerm, searchPages);

    const handleSearchTermChange = (searchQuery) => {
        if (!searchQuery || !searchQuery?.length) {
            replace("/blog", undefined, { shallow: true });
        } else {
            setSearchTermQuery((current) => ({
                ...current,
                upit: searchQuery,
            }));
        }
    };

    const isSearchMode = !!(searchTerm && !isSearching);

    // Search component
    const SingleSearchComponent = useCallback(
        () => (
            <SingleSearchSimple
                placeholder={searchTerm || "Pretraži..."}
                onClick={handleSearchTermChange}
                onEnterPress={handleSearchTermChange}
            />
        ),
        [],
    );

    const imageUrl = useImageUrl(isDesktop ? desktopImageId : otherDevicesImageId);

    const seoItem = useMemo(
        () => ({
            ...pages,
            title,
            description,
            url,
            articles: pages,
        }),
        [pages],
    );

    return (
        <Motion.Route {...rest}>
            <ListingSeo item={seoItem} image={imageUrl} />

            <NavigationTopBar searchComponent={SingleSearchComponent} />

            <Row variant="articles.hero">
                <Column sx={{ width: "100%" }}>
                    <HeadingSectionHero
                        imageId={isDesktop ? desktopImageId : otherDevicesImageId}
                        title={title}
                        lead={description}
                        mobileIsSmallLead
                        searchComponent={SingleSearchComponent}
                        disableSearchComponent={!isDesktop}
                        size={headingSectionHeroSizes.MEDIUM}
                    />
                </Column>
            </Row>

            {isSearchMode && (
                <Row variant="photo.heading.top">
                    <Column sx={{ width: "100%" }}>
                        <HeadingResultsSimple
                            label="Blog"
                            search={searchTerm}
                            onBackButtonClick={() => handleSearchTermChange("")}
                        />
                    </Column>
                </Row>
            )}

            {isSearchMode && !searchPages?.[0]?.length && !isSearching && (
                <SearchNoResults
                    title="Ups, nismo ništa pronašli :/"
                    text="Ovo se rijetko događa. Žao nam je! Probaj potražiti nešto drugo."
                />
            )}

            {searchPages?.map((articleGroup, groupIndex) => (
                <GridLayoutController
                    key={uid(groupIndex)}
                    items={articleGroup}
                    groupIndex={groupIndex}
                    noRow
                />
            ))}

            {pages?.map((articleGroup, groupIndex) => (
                <GridLayoutController
                    key={uid(groupIndex)}
                    items={articleGroup}
                    groupIndex={groupIndex}
                    noRow
                />
            ))}

            <InfiniteLoadTrigger ref={isSearchMode ? searchRef : ref} />

            <FooterSeparator />
        </Motion.Route>
    );
};

export const getStaticProps = storeWrapper.getStaticProps(async (ctx) => {
    const { dehydratedState } = await getListElastic({ resource: "blog", endpoint: "novo" });
    const initialProps = await initializeGlobalProps({
        ...ctx,
        dehydratedState,
        ignoreSession: true,
    });

    return {
        props: {
            ...initialProps,
        },
        revalidate: 5,
    };
});

export default Articles;
